/**
 * Brand Slice
 * Redux Toolkit slice for brand state management
 */

import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import type { Brand, CreateBrandRequest, UpdateBrandRequest } from '@creatoros/shared';
import { brandService } from '../../services/brand.service';
import { asArray } from '../../services/api.unwrap';
import { ApiErrorState } from '../../types/error.types';
import { logger } from '../../utils/logger';

/**
 * Brand State Interface
 */
export interface BrandState {
  brands: Brand[];
  /** Accurate total count from DB (not bounded by page size). Null until first fetch. */
  totalCount: number | null;
  selectedBrand: Brand | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: ApiErrorState | null;
  filters: {
    skuId?: string;
  };
}

/**
 * Initial State
 */
const initialState: BrandState = {
  brands: [],
  totalCount: null,
  selectedBrand: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  filters: {},
};

/**
 * Async Thunks
 */

/**
 * Fetch all brands
 */
export const fetchBrands = createAsyncThunk<
  { brands: Brand[]; total: number | null },
  void,
  { rejectValue: ApiErrorState }
>(
  'brands/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      logger.debug('Fetching brands', { event: 'brands.fetch.started' });
      const response = await brandService.list();
      const brands = asArray<Brand>(response.data);
      logger.debug('Fetched brands', {
        event: 'brands.fetch.succeeded',
        count: brands.length,
      });
      return {
        brands,
        total: response.total ?? null,
      };
    } catch (err: any) {
      // ApiClientError has code, message, details directly on the object
      logger.error('Failed to fetch brands', err, {
        event: 'brands.fetch.failed',
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to fetch brands',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  },
  {
    // Prevent duplicate in-flight fetches.  When multiple components dispatch
    // fetchBrands() simultaneously (e.g. DashboardPage + DashboardLayout), the
    // second dispatch is silently skipped while the first is still pending.
    condition: (_, { getState }) => {
      const { brands } = getState() as { brands: { isLoading: boolean } };
      return !brands.isLoading;
    },
  }
);

/**
 * Fetch a single brand by ID
 */
export const fetchBrandById = createAsyncThunk<Brand, string, { rejectValue: ApiErrorState }>(
  'brands/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      logger.debug('Fetching brand', { event: 'brands.fetch_by_id.started', brandId: id });
      const brand = await brandService.getById(id);
      logger.debug('Fetched brand', { event: 'brands.fetch_by_id.succeeded', brandId: id });
      return brand;
    } catch (err: any) {
      logger.error('Failed to fetch brand', err, {
        event: 'brands.fetch_by_id.failed',
        brandId: id,
        code: err.code || err.response?.data?.errorType,
      });
      return rejectWithValue({
        message: err.message || err.response?.data?.error || 'Failed to fetch brand',
        code: err.code || err.response?.data?.errorType,
        details: err.details || err.response?.data?.details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Create a new brand
 */
export const createBrand = createAsyncThunk<
  Brand,
  CreateBrandRequest,
  { rejectValue: ApiErrorState }
>('brands/create', async (data, { rejectWithValue }) => {
  try {
    const brand = await brandService.create(data);
    logger.info('Brand created', { event: 'brands.create.succeeded', brandId: brand.id });
    return brand;
  } catch (err: any) {
    logger.error('Failed to create brand', err, {
      event: 'brands.create.failed',
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to create brand',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Update an existing brand
 */
export const updateBrand = createAsyncThunk<
  Brand,
  { id: string; data: UpdateBrandRequest },
  { rejectValue: ApiErrorState }
>('brands/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const brand = await brandService.update(id, data);
    logger.info('Brand updated', { event: 'brands.update.succeeded', brandId: id });
    return brand;
  } catch (err: any) {
    logger.error('Failed to update brand', err, {
      event: 'brands.update.failed',
      brandId: id,
      code: err.code || err.response?.data?.errorType,
    });
    return rejectWithValue({
      message: err.message || err.response?.data?.error || 'Failed to update brand',
      code: err.code || err.response?.data?.errorType,
      details: err.details || err.response?.data?.details,
      timestamp: new Date().toISOString(),
    });
  }
});

/**
 * Delete a brand
 */
export const deleteBrand = createAsyncThunk<string, string, { rejectValue: ApiErrorState }>(
  'brands/delete',
  async (id, { rejectWithValue }) => {
    try {
      await brandService.delete(id);
      logger.info('Brand deleted', { event: 'brands.delete.succeeded', brandId: id });
      return id;
    } catch (err: any) {
      // ApiClientError has code, message, details directly on the object
      const code = err.code || err.response?.data?.errorType;
      const message = err.message || err.response?.data?.error || 'Failed to delete brand';
      const details = err.details || err.response?.data?.details;

      logger.error('Failed to delete brand', err, {
        event: 'brands.delete.failed',
        brandId: id,
        code,
      });
      return rejectWithValue({
        message,
        code,
        details,
        timestamp: new Date().toISOString(),
      });
    }
  }
);

/**
 * Brand Slice
 */
const brandSlice = createSlice({
  name: 'brands',
  initialState,
  reducers: {
    /**
     * Set selected brand
     */
    setSelectedBrand: (state, action: PayloadAction<Brand | null>) => {
      state.selectedBrand = action.payload;
    },

    /**
     * Clear selected brand
     */
    clearSelectedBrand: (state) => {
      state.selectedBrand = null;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Reset state
     */
    resetBrandState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch all brands
      .addCase(fetchBrands.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.isLoading = false;
        state.brands = asArray(action.payload.brands);
        state.totalCount = action.payload.total;
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch brands' };
      })

      // Fetch brand by ID
      .addCase(fetchBrandById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBrandById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedBrand = action.payload;
        // Update in list if exists
        const index = state.brands.findIndex((b) => b.id === action.payload.id);
        if (index !== -1) {
          state.brands[index] = action.payload;
        }
      })
      .addCase(fetchBrandById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? { message: 'Failed to fetch brand' };
      })

      // Create brand
      .addCase(createBrand.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createBrand.fulfilled, (state, action) => {
        state.isCreating = false;
        state.brands.push(action.payload);
        if (state.totalCount !== null) {
          state.totalCount += 1;
        }
      })
      .addCase(createBrand.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? { message: 'Failed to create brand' };
      })

      // Update brand
      .addCase(updateBrand.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateBrand.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.brands.findIndex((b) => b.id === action.payload.id);
        if (index !== -1) {
          state.brands[index] = action.payload;
        }
        if (state.selectedBrand?.id === action.payload.id) {
          state.selectedBrand = action.payload;
        }
      })
      .addCase(updateBrand.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? { message: 'Failed to update brand' };
      })

      // Delete brand
      .addCase(deleteBrand.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteBrand.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.brands = state.brands.filter((b) => b.id !== action.payload);
        if (state.selectedBrand?.id === action.payload) {
          state.selectedBrand = null;
        }
        if (state.totalCount !== null) {
          state.totalCount -= 1;
        }
      })
      .addCase(deleteBrand.rejected, (state, action) => {
        state.isDeleting = false;
        // Cast to match ApiErrorState structure if needed, though payload is already aligned
        state.error = (action.payload as ApiErrorState) ?? { message: 'Failed to delete brand' };
      });
  },
});

/**
 * Selectors
 */

// Base selectors
export const selectBrands = (state: RootState) => asArray<Brand>(state.brands.brands);
export const selectSelectedBrand = (state: RootState) => state.brands.selectedBrand;
export const selectBrandError = (state: RootState) => state.brands.error;
export const selectBrandIsLoading = (state: RootState) => state.brands.isLoading;
export const selectBrandIsCreating = (state: RootState) => state.brands.isCreating;
export const selectBrandIsUpdating = (state: RootState) => state.brands.isUpdating;
export const selectBrandIsDeleting = (state: RootState) => state.brands.isDeleting;

// Derived selectors
export const selectBrandById = (state: RootState, id: string) =>
  selectBrands(state).find((brand) => brand.id === id);

export const selectBrandsByStatus = (state: RootState, status: string) =>
  selectBrands(state).filter((brand) => brand.relationshipStatus === status);

export const selectActiveBrands = (state: RootState) =>
  selectBrands(state).filter((brand) => brand.relationshipStatus === 'active');

/**
 * Returns the accurate total count from the database when available,
 * falling back to the length of the locally-loaded page.
 */
export const selectBrandsCount = (state: RootState) =>
  state.brands.totalCount ?? selectBrands(state).length;

export const selectBrandsTotalValue = (state: RootState) =>
  selectBrands(state).reduce((total, brand) => total + brand.totalValue, 0);

/**
 * Selector: Top 5 brands sorted by total deal value
 * Memoized to prevent new array references on every call
 */
export const selectTopBrandsByValue = createSelector([selectBrands], (brands) =>
  [...brands].sort((a, b) => b.totalValue - a.totalValue).slice(0, 5)
);

/**
 * Export actions and reducer
 */
export const { setSelectedBrand, clearSelectedBrand, clearError, resetBrandState } =
  brandSlice.actions;

export default brandSlice.reducer;
